import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { DefaultEventTypeCostsModel, DefaultEventTypeProcessModel } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FormNumericInput, FormRadio, FormSwitch, FormTextArea } from "components/form-fields";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";

export function EventTypeCost({
    model = DefaultEventTypeCostsModel,
    forceLoad = false,
    processModel = DefaultEventTypeProcessModel,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeCostsModel; },
    onSave = () => { },
    onForceLoadFinished = () => { }
}) {
    var navigate = useNavigate();

    const step = 5;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);
    
    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    const [originalCostsModel, setOriginalCostsModel] = useState(DefaultEventTypeCostsModel);
    const [costsModel, setCostsModel] = useState(DefaultEventTypeCostsModel);

    const [isValid, costsModelErrors, validateCostsModel] = useValidation();
    const isCostModelDirty = useDirtyData(originalCostsModel, costsModel);

    const [radioOptions, setRadioOptions] = useState([
        { value: "1", label: "Not required" },
        { value: "2", label: "Required" }
    ]);

    useEffect(() => {
        const options = [
            { value: "1", label: "Not required" },
            { value: "2", label: "Required" }
        ];

        if (processModel && processModel.failStatusEnabled) {
            options.push({ value: "3", label: "Only required for failed events" });
        }
        
        setRadioOptions(options);
    }, [processModel && processModel.failStatusEnabled]);

    useEffect(() => {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }

        if (
            !isInWizardMode &&
            (
                model.includeCosts !== DefaultEventTypeCostsModel.includeCosts ||
                model.requiresCosts !== DefaultEventTypeCostsModel.requiresCosts ||
                model.requiresCostsFailedOnly !== DefaultEventTypeCostsModel.requiresCostsFailedOnly ||
                model.requiresCostsValue !== DefaultEventTypeCostsModel.requiresCostsValue ||
                model.costHelperText !== DefaultEventTypeCostsModel.costHelperText ||
                model.includeSuppliers !== DefaultEventTypeCostsModel.includeSuppliers ||
                model.requiresSuppliers !== DefaultEventTypeCostsModel.requiresSuppliers ||
                model.includeCostDocumentation !== DefaultEventTypeCostsModel.includeCostDocumentation ||
                model.requiresCostDocumentationCount !== DefaultEventTypeCostsModel.requiresCostDocumentationCount
            )
        ) {
            setOriginalCostsModel(model);
        } 

        setInitialLoadCompleted(true);
        
        setCostsModel(prev => { return {...prev, ...model}; });

        if (forceLoad) {
            onForceLoadFinished();
        }
    }, [model, forceLoad]);

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }
        
        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(validate, [costsModel])

    function validate() {
        let validationRules = [];

        if (costsModel.includeCosts) {
            validationRules.push({
                fieldName: "costHelperText",
                maxLength: 1000
            });

            validationRules.push({
                fieldName: "requiresCostDocumentationCount",
                required: costsModel.includeCostDocumentation,
                type: 'number'
            })
        }

        validateCostsModel(validationRules, costsModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, value) {
        let updatedModel = {
            ...costsModel,
            [fieldName]: value
        };

        if (fieldName === "includeSuppliers" && value === false) {
            updatedModel.requiresSuppliers = false;
        }

        setCostsModel(updatedModel);
        onModelUpdate(step, updatedModel);
    }

    function onRequiresCostsValueChange(item) {
        const value = item.currentTarget.value;
        let requiresCosts = false;
        let requiresCostsFailedOnly = false;

        if (value === "2") {
            requiresCosts = true;
        } else if (value === "3") {
            requiresCosts = true;
            requiresCostsFailedOnly = true;
        }

        const clonedModel = {
            ...costsModel,
            requiresCostsValue: value,
            requiresCosts,
            requiresCostsFailedOnly
        };

        setCostsModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function onConfirmLeave(redirectToPath) {
        setCostsModel(originalCostsModel);
        onModelUpdate(step, originalCostsModel);

        setTimeout(() => navigate(redirectToPath));
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Costs</h2>
                                <AlertUnsavedData isDirty={isCostModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <FormSwitch
                        disabled={saving}
                        loading={loading}
                        headingText="Enable costs"
                        checked={costsModel.includeCosts}
                        onChange={e => onValueChange("includeCosts", e.target.checked)}
                    />

                    <ShowHide
                        evaluator={costsModel?.includeCosts === true}
                        show={
                            <div>
                                <ShowHide
                                    evaluator={processModel.failStatusEnabled}
                                    show={
                                        <FormSwitch
                                            disabled={saving}
                                            loading={loading}
                                            headingText="Require costs"
                                            checked={costsModel.requiresCosts}
                                            onChange={e => onValueChange("requiresCosts", e.target.checked)}
                                        />
                                    }
                                    hide={<div>
                                        <FormRadio
                                            headingText="Please select the circumstances where costs are required"
                                            options={radioOptions}
                                            selectedValue={costsModel.requiresCostsValue || "1"}
                                            onChange={onRequiresCostsValueChange}
                                            disabled={saving}
                                            loading={loading}
                                        />
                                    </div>}
                                />
                                <FormTextArea
                                    disabled={saving}
                                    loading={loading}
                                    headingText="Cost comment"
                                    value={costsModel.costHelperText}
                                    onChange={e => onValueChange("costHelperText", e.target.value)}
                                    maxLength={4000}
                                    dangerHelperText={costsModelErrors["costHelperText"]}
                                />
                                <FormSwitch
                                    disabled={saving}
                                    loading={loading}
                                    headingText="Enable suppliers"
                                    checked={costsModel.includeSuppliers}
                                    onChange={e => onValueChange("includeSuppliers", e.target.checked)}
                                />
                                <ShowHide
                                    evaluator={costsModel.includeSuppliers}
                                    show={
                                        <FormSwitch
                                            disabled={saving}
                                            loading={loading}
                                            headingText="Require suppliers"
                                            checked={costsModel.requiresSuppliers}
                                            onChange={e => onValueChange("requiresSuppliers", e.target.checked)}
                                        />
                                    }
                                />
                                <FormSwitch
                                    disabled={saving}
                                    loading={loading}
                                    headingText="Enable cost document attachment"
                                    checked={costsModel.includeCostDocumentation}
                                    onChange={e => onValueChange("includeCostDocumentation", e.target.checked)}
                                />
                                <ShowHide
                                    evaluator={costsModel.includeCostDocumentation}
                                    show={
                                        <FormNumericInput
                                            disabled={saving}
                                            loading={loading}
                                            headingText="Set minimum number of required cost documents"
                                            selectedValue={costsModel.requiresCostDocumentationCount}
                                            onValueChange={e => onValueChange("requiresCostDocumentationCount", e)}
                                            min={0}
                                            max={50}
                                            dangerHelperText={costsModelErrors["requiresCostDocumentationCount"]}
                                        />
                                    }                                
                                />
                            </div>
                        }
                    />

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <ButtonSave
                                loading={loading}
                                onClick={() => onSave("costs")}
                                disabled={saving}
                                simpleDisabled={!isCostModelDirty || !isValid}
                            />	
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeCost.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    processModel: PropTypes.object,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func
};
